import { motion, useReducedMotion } from 'framer-motion';
import type { ComponentType, ReactNode } from 'react';

interface OrbitItem {
  icon: ComponentType<{ className?: string; style?: React.CSSProperties }>;
  label: string;
}

interface OrbitRingProps {
  items: OrbitItem[];
  /** Icon for the governed core at the centre. Ignored when `center` is given. */
  centerIcon?: ComponentType<{ className?: string; style?: React.CSSProperties }>;
  centerLabel?: string;
  /** Custom centre content (e.g. a RotatingCube) replacing the default core node. */
  center?: ReactNode;
}

// C2 "Circular_graphics" from animation.md — a dashed orbit ring with product
// surfaces arranged around a governed core. The dashed ring rotates slowly while
// the nodes stay upright and hover-scale. Recolored to navy/cyan, reduced-motion
// aware. Scales to however many items are passed in (evenly distributed).
export function OrbitRing({ items, centerIcon: CenterIcon, centerLabel, center }: OrbitRingProps) {
  const reduce = useReducedMotion();
  const radius = 42; // percent of container half-size

  return (
    <div className="relative mx-auto aspect-square w-full max-w-md">
      {/* rotating dashed ring */}
      <motion.div
        className="absolute inset-[8%] rounded-full border-2 border-dashed border-[#074EDC]/30"
        animate={reduce ? undefined : { rotate: 360 }}
        transition={reduce ? undefined : { duration: 48, repeat: Infinity, ease: 'linear' }}
      />
      <div className="absolute inset-[20%] rounded-full border border-[#074EDC]/15" />

      {/* governed core */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        {center ? (
          center
        ) : (
          <div className="flex flex-col items-center gap-2 rounded-2xl border border-[#074EDC]/40 bg-slate-900/80 backdrop-blur-md px-5 py-4 text-center shadow-[0_0_40px_-10px_rgba(7,78,220,0.5)]">
            {CenterIcon && (
              <span className="inline-flex rounded-xl bg-gradient-to-br from-[#074EDC]/20 to-[#4AAC00]/20 p-3">
                <CenterIcon className="h-6 w-6 text-[#074EDC]" />
              </span>
            )}
            {centerLabel && <span className="max-w-[7rem] text-white/80">{centerLabel}</span>}
          </div>
        )}
      </div>

      {/* orbiting surfaces */}
      {items.map(({ icon: Icon, label }, i) => {
        const angle = (i / items.length) * 2 * Math.PI - Math.PI / 2;
        const left = 50 + radius * Math.cos(angle);
        const top = 50 + radius * Math.sin(angle);
        const logoColors = ['#074EDC', '#FF8C00', '#4AAC00', '#ED0216'];
        const c = logoColors[i % logoColors.length];
        return (
          <motion.div
            key={label}
            className="absolute flex w-24 -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-1.5 text-center"
            style={{ left: `${left}%`, top: `${top}%` }}
            whileHover={{ scale: 1.12 }}
            transition={{ type: 'spring', stiffness: 300, damping: 18 }}
          >
            <span className="inline-flex rounded-xl border bg-slate-800/80 backdrop-blur-sm p-3" style={{ borderColor: `${c}4d` }}>
              <Icon className="h-5 w-5" style={{ color: c }} />
            </span>
            <span className="text-white/75 text-xs leading-tight">{label}</span>
          </motion.div>
        );
      })}
    </div>
  );
}
